import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/lib/auth'
import {
  FolderKanban,
  Plus,
  MoreHorizontal,
  Calendar,
  User,
  ChevronDown,
  Search,
  Filter,
  Loader2,
  X,
  Paperclip,
  Link2,
  MessageSquare,
  Circle,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  SignalHigh,
  SignalMedium,
  SignalLow,
  Minus,
  LayoutGrid,
  List,
  GripVertical,
  ChevronRight,
  Users,
  Tag,
  ExternalLink,
  Copy,
  Trash2,
  Archive,
  Layers,
  GitBranch,
  ArrowLeft,
  ArrowRight,
  Maximize2,
  Bell,
  Share2,
  BarChart3,
  FileText,
  UserCircle,
  CalendarDays,
  Boxes,
  RefreshCw,
  Hash,
} from 'lucide-react'
import { getPlaneClient } from '@/lib/plane/client'
import {
  PlaneProject,
  PlaneIssue,
  PlaneState,
  PlaneLabel,
  PlaneCycle,
} from '@/lib/plane/types'
import { getProjectColor, PROJECT_COLORS } from '@/lib/plane/colors'

interface ProjectsViewProps {
  orgSlug?: string
  selectedProjectId?: string | 'all'
  onProjectChange?: (projectId: string | 'all') => void
}

// ============ CONFIGURATION ============
const PLANE_BASE_URL = import.meta.env.VITE_PLANE_API_URL || 'http://10.0.0.244:8080'
const PLANE_WORKSPACE = import.meta.env.VITE_PLANE_WORKSPACE_SLUG || 'nexus'

// ============ HELPERS ============
// Strip HTML tags from description
function stripHtml(html: string | null): string {
  if (!html) return ''
  return html.replace(/<[^>]*>/g, '').trim()
}

// ============ PRIORITY CONFIGURATION ============
type PriorityKey = 'urgent' | 'high' | 'medium' | 'low' | 'none'

const PRIORITY_CONFIG: Record<PriorityKey, {
  icon: typeof AlertCircle
  color: string
  bg: string
  label: string
}> = {
  urgent: {
    icon: AlertCircle,
    color: 'var(--priority-urgent)',
    bg: 'rgba(239, 68, 68, 0.15)',
    label: 'Urgent'
  },
  high: {
    icon: SignalHigh,
    color: 'var(--priority-high)',
    bg: 'rgba(249, 115, 22, 0.15)',
    label: 'High'
  },
  medium: {
    icon: SignalMedium,
    color: 'var(--priority-medium)',
    bg: 'rgba(234, 179, 8, 0.15)',
    label: 'Medium'
  },
  low: {
    icon: SignalLow,
    color: 'var(--priority-low)',
    bg: 'rgba(59, 130, 246, 0.15)',
    label: 'Low'
  },
  none: {
    icon: Minus,
    color: 'var(--priority-none)',
    bg: 'rgba(107, 114, 128, 0.15)',
    label: 'None'
  },
}

// ============ STATE GROUP CONFIGURATION ============
type StateGroup = 'backlog' | 'unstarted' | 'started' | 'completed' | 'cancelled'

const STATE_GROUP_CONFIG: Record<StateGroup, {
  icon: typeof Circle
  color: string
  bgColor: string
  label: string
}> = {
  backlog: {
    icon: Circle,
    color: 'var(--state-backlog)',
    bgColor: 'rgba(107, 114, 128, 0.15)',
    label: 'Backlog'
  },
  unstarted: {
    icon: Circle,
    color: 'var(--state-unstarted)',
    bgColor: 'rgba(59, 130, 246, 0.15)',
    label: 'Todo'
  },
  started: {
    icon: Clock,
    color: 'var(--state-started)',
    bgColor: 'rgba(234, 179, 8, 0.15)',
    label: 'In Progress'
  },
  completed: {
    icon: CheckCircle2,
    color: 'var(--state-completed)',
    bgColor: 'rgba(34, 197, 94, 0.15)',
    label: 'Done'
  },
  cancelled: {
    icon: XCircle,
    color: 'var(--state-cancelled)',
    bgColor: 'rgba(239, 68, 68, 0.15)',
    label: 'Cancelled'
  },
}

// ============ HELPER FUNCTIONS ============
const extractArray = <T,>(data: any): T[] => {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (data.results && Array.isArray(data.results)) return data.results
  return []
}

const getPriorityConfig = (priority: string) => {
  return PRIORITY_CONFIG[priority as PriorityKey] || PRIORITY_CONFIG.none
}

const getStateGroupConfig = (group: string) => {
  return STATE_GROUP_CONFIG[group as StateGroup] || STATE_GROUP_CONFIG.unstarted
}

// ============ MAIN COMPONENT ============
export function ProjectsView({ orgSlug, selectedProjectId = 'all', onProjectChange }: ProjectsViewProps) {
  // State
  const [projects, setProjects] = useState<PlaneProject[]>([])
  const [selectedProject, setSelectedProject] = useState<PlaneProject | null>(null)
  const [issues, setIssues] = useState<PlaneIssue[]>([])
  const [states, setStates] = useState<PlaneState[]>([])
  const [labels, setLabels] = useState<PlaneLabel[]>([])
  const [cycles, setCycles] = useState<PlaneCycle[]>([])
  const [allProjectsMap, setAllProjectsMap] = useState<Map<string, PlaneProject>>(new Map())

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingIssues, setIsLoadingIssues] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [priorityFilter, setPriorityFilter] = useState<string>('')
  const [projectFilter, setProjectFilter] = useState<string>('')
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban')

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedIssue, setSelectedIssue] = useState<PlaneIssue | null>(null)
  const [quickAddGroup, setQuickAddGroup] = useState<string | null>(null)

  const client = getPlaneClient()
  const isConfigured = client.isConfigured()
  const isAllProjectsMode = selectedProjectId === 'all'

  // Fetch projects on mount
  const fetchProjects = useCallback(async () => {
    if (!isConfigured) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    const { data, error: apiError } = await client.getProjects()

    if (apiError) {
      setError(apiError.detail || apiError.error)
    } else if (data) {
      const projectList = extractArray<PlaneProject>(data)
      setProjects(projectList)

      // Build projects map for quick lookup
      const projectsMap = new Map<string, PlaneProject>()
      projectList.forEach(p => projectsMap.set(p.id, p))
      setAllProjectsMap(projectsMap)

      // Set selected project based on selectedProjectId prop
      if (selectedProjectId !== 'all') {
        const project = projectList.find(p => p.id === selectedProjectId)
        if (project) {
          setSelectedProject(project)
        } else if (projectList.length > 0) {
          setSelectedProject(projectList[0])
        }
      } else if (projectList.length > 0 && !selectedProject) {
        // For "all" mode, still set a default project for creating issues
        setSelectedProject(projectList[0])
      }
    }

    setIsLoading(false)
  }, [isConfigured, selectedProjectId])

  // Fetch issues for selected project or ALL projects
  const fetchIssues = useCallback(async (projectId: string | 'all') => {
    setIsLoadingIssues(true)

    if (projectId === 'all') {
      // Fetch issues from ALL projects in parallel
      const allIssues: PlaneIssue[] = []
      const allStates: PlaneState[] = []
      const allLabels: PlaneLabel[] = []
      const seenStateIds = new Set<string>()
      const seenLabelIds = new Set<string>()

      await Promise.all(
        projects.map(async (project) => {
          const [issuesRes, statesRes, labelsRes] = await Promise.all([
            client.getIssues(project.id),
            client.getStates(project.id),
            client.getLabels(project.id),
          ])

          const projectIssues = extractArray<PlaneIssue>(issuesRes.data?.results || issuesRes.data)
          allIssues.push(...projectIssues)

          // Collect unique states and labels
          const projectStates = extractArray<PlaneState>(statesRes.data)
          projectStates.forEach(state => {
            if (!seenStateIds.has(state.id)) {
              seenStateIds.add(state.id)
              allStates.push(state)
            }
          })

          const projectLabels = extractArray<PlaneLabel>(labelsRes.data)
          projectLabels.forEach(label => {
            if (!seenLabelIds.has(label.id)) {
              seenLabelIds.add(label.id)
              allLabels.push(label)
            }
          })
        })
      )

      setIssues(allIssues)
      setStates(allStates)
      setLabels(allLabels)
    } else {
      // Fetch issues for single project
      const [issuesRes, statesRes, labelsRes, cyclesRes] = await Promise.all([
        client.getIssues(projectId),
        client.getStates(projectId),
        client.getLabels(projectId),
        client.getCycles(projectId),
      ])

      setIssues(extractArray<PlaneIssue>(issuesRes.data?.results || issuesRes.data))
      setStates(extractArray<PlaneState>(statesRes.data))
      setLabels(extractArray<PlaneLabel>(labelsRes.data))
      setCycles(extractArray<PlaneCycle>(cyclesRes.data))
    }

    setIsLoadingIssues(false)
  }, [projects])

  useEffect(() => {
    fetchProjects()
  }, [])

  // Fetch issues when selectedProjectId changes
  useEffect(() => {
    if (isAllProjectsMode && projects.length > 0) {
      fetchIssues('all')
    } else if (selectedProject?.id) {
      fetchIssues(selectedProject.id)
    }
  }, [selectedProjectId, selectedProject, projects.length, fetchIssues, isAllProjectsMode])

  // Group issues by state group
  const issuesByStateGroup = issues.reduce((acc, issue) => {
    const stateGroup = issue.state_detail?.group || 'unstarted'
    if (!acc[stateGroup]) {
      acc[stateGroup] = []
    }
    // Apply filters
    if (searchQuery && !issue.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return acc
    }
    if (priorityFilter && issue.priority !== priorityFilter) {
      return acc
    }
    // Project filter (for "all" mode)
    if (isAllProjectsMode && projectFilter && issue.project !== projectFilter) {
      return acc
    }
    acc[stateGroup].push(issue)
    return acc
  }, {} as Record<string, PlaneIssue[]>)

  // Handle issue state change
  const handleStateChange = async (issueId: string, newStateId: string) => {
    if (!selectedProject) return

    await client.updateIssue(selectedProject.id, issueId, { state: newStateId })
    fetchIssues(selectedProject.id)
  }

  // Handle create task
  const handleCreateTask = async (data: {
    projectId: string
    name: string
    description?: string
    priority?: string
    state?: string
    labels?: string[]
    start_date?: string
    target_date?: string
  }) => {
    const defaultState = states.find(s => s.group === 'unstarted')

    await client.createIssue(data.projectId, {
      name: data.name,
      description: data.description || '',
      priority: (data.priority as any) || 'none',
      state: data.state || defaultState?.id,
      labels: data.labels as any,
      start_date: data.start_date,
      target_date: data.target_date,
    })

    setShowCreateModal(false)
    setQuickAddGroup(null)

    // Refresh the right view
    if (isAllProjectsMode) {
      fetchIssues('all')
    } else {
      fetchIssues(data.projectId)
    }
  }

  // Legacy handler for quick add (keeps old signature)
  const handleCreateIssue = async (data: {
    name: string
    description?: string
    priority?: string
    state?: string
  }) => {
    if (!selectedProject) return
    await handleCreateTask({ projectId: selectedProject.id, ...data })
  }

  // ============ RENDER: Not Configured ============
  if (!isConfigured) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-[var(--bg-element)] flex items-center justify-center mb-4">
          <FolderKanban size={32} className="text-[var(--text-muted)]" />
        </div>
        <h3 className="text-lg font-semibold text-[var(--text-main)] mb-2">Plane.so Not Configured</h3>
        <p className="text-sm text-[var(--text-muted)] text-center max-w-md">
          Add VITE_PLANE_API_URL and VITE_PLANE_API_KEY to your environment variables to connect to Plane.so
        </p>
      </div>
    )
  }

  // ============ RENDER: Loading ============
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          <span className="text-sm text-[var(--text-muted)]">Loading projects...</span>
        </div>
      </div>
    )
  }

  // ============ RENDER: Error ============
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4">
          <AlertCircle size={32} className="text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-[var(--text-main)] mb-2">Error Loading Projects</h3>
        <p className="text-sm text-[var(--text-muted)] mb-4">{error}</p>
        <button
          onClick={fetchProjects}
          className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Try Again
        </button>
      </div>
    )
  }

  // ============ RENDER: Main View ============
  return (
    <div className="h-full flex flex-col">
      {/* ============ HEADER ============ */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Project Selector */}
          <div className="relative group">
            <button className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg hover:border-[var(--border-strong)] transition-colors">
              {isAllProjectsMode ? (
                <>
                  <Layers size={14} className="text-[var(--primary)]" />
                  <span className="text-sm font-medium text-[var(--text-main)]">
                    All Issues
                  </span>
                  <span className="text-xs text-[var(--text-muted)] bg-[var(--bg-element)] px-1.5 py-0.5 rounded">
                    {issues.length}
                  </span>
                </>
              ) : (
                <>
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: selectedProject ? getProjectColor(selectedProject.name) : '#6b7280' }}
                  />
                  <span className="text-sm font-medium text-[var(--text-main)]">
                    {selectedProject?.name || 'Select Project'}
                  </span>
                </>
              )}
              <ChevronDown size={16} className="text-[var(--text-muted)]" />
            </button>

            {/* Dropdown */}
            <div className="absolute top-full left-0 mt-1 w-64 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="p-2">
                {/* All Issues option */}
                <button
                  onClick={() => {
                    onProjectChange?.('all')
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                    isAllProjectsMode
                      ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                      : 'hover:bg-[var(--bg-element)] text-[var(--text-main)]'
                  }`}
                >
                  <Layers size={14} />
                  <span className="text-sm">All Issues</span>
                  {isAllProjectsMode && (
                    <CheckCircle2 size={14} className="ml-auto" />
                  )}
                </button>

                <div className="border-t border-[var(--border)] my-1" />

                {projects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => {
                      setSelectedProject(project)
                      onProjectChange?.(project.id)
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                      !isAllProjectsMode && selectedProject?.id === project.id
                        ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                        : 'hover:bg-[var(--bg-element)] text-[var(--text-main)]'
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: getProjectColor(project.name) }}
                    />
                    <span className="text-sm">{project.name}</span>
                    {!isAllProjectsMode && selectedProject?.id === project.id && (
                      <CheckCircle2 size={14} className="ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Project Filter (in All mode) */}
          {isAllProjectsMode && (
            <div className="relative group">
              <button className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] hover:border-[var(--border-strong)] transition-colors">
                <FolderKanban size={14} />
                {projectFilter ? allProjectsMap.get(projectFilter)?.name : 'All Projects'}
                {projectFilter && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
                )}
              </button>

              <div className="absolute top-full left-0 mt-1 w-48 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="p-2">
                  <button
                    onClick={() => setProjectFilter('')}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                      !projectFilter
                        ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                        : 'text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                    }`}
                  >
                    All Projects
                  </button>
                  {projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => setProjectFilter(project.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                        projectFilter === project.id
                          ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                          : 'text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                      }`}
                    >
                      <div
                        className="w-2.5 h-2.5 rounded"
                        style={{ backgroundColor: getProjectColor(project.name) }}
                      />
                      {project.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* View Toggle */}
          <div className="flex items-center bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
              }`}
            >
              <LayoutGrid size={14} />
              Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
              }`}
            >
              <List size={14} />
              List
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={14} />
            <input
              type="text"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-56 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg pl-9 pr-3 py-2 text-sm text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary)] transition-colors"
            />
          </div>

          {/* Filter */}
          <div className="relative group">
            <button className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] hover:border-[var(--border-strong)] transition-colors">
              <Filter size={14} />
              Filters
              {priorityFilter && (
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
              )}
            </button>

            <div className="absolute top-full right-0 mt-1 w-48 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="p-2">
                <p className="px-2 py-1 text-xs font-medium text-[var(--text-muted)] uppercase">Priority</p>
                {(['', 'urgent', 'high', 'medium', 'low', 'none'] as const).map(p => (
                  <button
                    key={p}
                    onClick={() => setPriorityFilter(p)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                      priorityFilter === p
                        ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                        : 'text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                    }`}
                  >
                    {p ? (
                      <>
                        {(() => {
                          const config = getPriorityConfig(p)
                          const Icon = config.icon
                          return <Icon size={14} style={{ color: config.color }} />
                        })()}
                        <span>{getPriorityConfig(p).label}</span>
                      </>
                    ) : (
                      <span>All Priorities</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Create Task Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus size={16} />
            New Task
          </button>

          {/* Open in Plane */}
          <a
            href={`${PLANE_BASE_URL}/${PLANE_WORKSPACE}/projects/${selectedProject?.id}/issues`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
            title="Open in Plane.so"
          >
            <ExternalLink size={16} />
          </a>
        </div>
      </div>

      {/* ============ STATS BAR ============ */}
      <div className="flex items-center gap-6 mb-6 px-1">
        {(['backlog', 'unstarted', 'started', 'completed', 'cancelled'] as const).map(group => {
          const config = getStateGroupConfig(group)
          const count = issuesByStateGroup[group]?.length || 0
          const Icon = config.icon
          return (
            <div key={group} className="flex items-center gap-2">
              <Icon size={14} style={{ color: config.color }} />
              <span className="text-sm text-[var(--text-muted)]">{config.label}</span>
              <span className="text-sm font-medium text-[var(--text-main)]">{count}</span>
            </div>
          )
        })}
      </div>

      {/* ============ KANBAN VIEW ============ */}
      {viewMode === 'kanban' && (
        <div className="flex-1 pb-4 min-h-0">
          <div className="flex gap-3 h-full w-full">
            {(['backlog', 'unstarted', 'started', 'completed', 'cancelled'] as const).map(group => {
              const config = getStateGroupConfig(group)
              const groupIssues = issuesByStateGroup[group] || []
              const Icon = config.icon

              return (
                <div
                  key={group}
                  className="flex-1 min-w-[200px] flex flex-col bg-[var(--bg-element)]/30 rounded-lg"
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Icon size={16} style={{ color: config.color }} />
                      <span className="text-sm font-medium text-[var(--text-main)]">{config.label}</span>
                      <span className="text-xs text-[var(--text-muted)] bg-[var(--bg-card)] px-2 py-0.5 rounded-full">
                        {groupIssues.length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setQuickAddGroup(group)}
                        className="p-1 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-card)] rounded transition-colors"
                        title="Add task"
                      >
                        <Plus size={16} />
                      </button>
                      <button className="p-1 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-card)] rounded transition-colors">
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                  </div>

                  {/* New Task Button */}
                  {quickAddGroup === group ? (
                    <div className="px-2 pb-2">
                      <QuickAddIssue
                        onSubmit={(name) => {
                          const stateForGroup = states.find(s => s.group === group)
                          handleCreateIssue({ name, state: stateForGroup?.id })
                        }}
                        onCancel={() => setQuickAddGroup(null)}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => setQuickAddGroup(group)}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                    >
                      <Plus size={14} />
                      <span>New Task</span>
                    </button>
                  )}

                  {/* Issues List */}
                  <div className="flex-1 overflow-y-auto px-2 space-y-2 custom-scrollbar">
                    {isLoadingIssues ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-[var(--text-muted)]" />
                      </div>
                    ) : groupIssues.length === 0 ? null : (
                      groupIssues.map(issue => {
                        const issueProject = isAllProjectsMode
                          ? allProjectsMap.get(issue.project)
                          : selectedProject
                        return (
                          <IssueCard
                            key={issue.id}
                            issue={issue}
                            projectIdentifier={issueProject?.identifier}
                            projectName={issueProject?.name}
                            projectColor={getProjectColor(issueProject?.name || '')}
                            showProject={isAllProjectsMode}
                            onClick={() => setSelectedIssue(issue)}
                          />
                        )
                      })
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ============ LIST VIEW ============ */}
      {viewMode === 'list' && (
        <div className="flex-1 overflow-auto">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
            {/* Table Header */}
            <div className="flex items-center gap-4 px-4 py-3 border-b border-[var(--border)] bg-[var(--bg-element)]/50">
              <div className="w-8" />
              <div className="flex-1 text-xs font-medium text-[var(--text-muted)] uppercase">Issue</div>
              <div className="w-24 text-xs font-medium text-[var(--text-muted)] uppercase">Status</div>
              <div className="w-24 text-xs font-medium text-[var(--text-muted)] uppercase">Priority</div>
              <div className="w-28 text-xs font-medium text-[var(--text-muted)] uppercase">Due Date</div>
              <div className="w-24 text-xs font-medium text-[var(--text-muted)] uppercase">Assignee</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-[var(--border)]">
              {isLoadingIssues ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-[var(--text-muted)]" />
                </div>
              ) : issues.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-[var(--text-muted)]">No issues found</p>
                </div>
              ) : (
                issues
                  .filter(issue => {
                    if (searchQuery && !issue.name.toLowerCase().includes(searchQuery.toLowerCase())) return false
                    if (priorityFilter && issue.priority !== priorityFilter) return false
                    return true
                  })
                  .map(issue => {
                    const priorityConfig = getPriorityConfig(issue.priority)
                    const stateConfig = getStateGroupConfig(issue.state_detail?.group || 'unstarted')
                    const PriorityIcon = priorityConfig.icon
                    const StateIcon = stateConfig.icon

                    return (
                      <div
                        key={issue.id}
                        onClick={() => setSelectedIssue(issue)}
                        className="flex items-center gap-4 px-4 py-3 hover:bg-[var(--bg-element)]/30 cursor-pointer transition-colors group"
                      >
                        <div className="w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                          <GripVertical size={16} className="text-[var(--text-muted)]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-[var(--text-muted)]">
                              {selectedProject?.identifier}-{issue.sequence_id}
                            </span>
                            <span className="text-sm text-[var(--text-main)] truncate">{issue.name}</span>
                          </div>
                        </div>
                        <div className="w-24">
                          <div className="flex items-center gap-1.5">
                            <StateIcon size={14} style={{ color: stateConfig.color }} />
                            <span className="text-xs text-[var(--text-muted)]">{issue.state_detail?.name}</span>
                          </div>
                        </div>
                        <div className="w-24">
                          <div
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                            style={{ backgroundColor: priorityConfig.bg }}
                          >
                            <PriorityIcon size={12} style={{ color: priorityConfig.color }} />
                            <span style={{ color: priorityConfig.color }}>{priorityConfig.label}</span>
                          </div>
                        </div>
                        <div className="w-28 text-sm text-[var(--text-muted)]">
                          {issue.target_date ? new Date(issue.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                        </div>
                        <div className="w-24">
                          {issue.assignees && issue.assignees.length > 0 ? (
                            <div className="flex -space-x-1">
                              {issue.assignees.slice(0, 3).map(assignee => (
                                <div
                                  key={assignee.id}
                                  className="w-6 h-6 rounded-full bg-[var(--primary)] flex items-center justify-center text-[10px] text-white border-2 border-[var(--bg-card)]"
                                  title={`${assignee.first_name} ${assignee.last_name}`}
                                >
                                  {assignee.first_name?.[0]}{assignee.last_name?.[0]}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[var(--text-muted)]">-</span>
                          )}
                        </div>
                      </div>
                    )
                  })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ MODALS ============ */}
      {showCreateModal && (
        <CreateTaskModal
          projects={isAllProjectsMode ? Array.from(allProjectsMap.values()) : (selectedProject ? [selectedProject] : [])}
          states={states}
          labels={labels}
          defaultProjectId={selectedProject?.id}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateTask}
        />
      )}

      {selectedIssue && (
        <IssueDetailModal
          issue={selectedIssue}
          projectIdentifier={
            isAllProjectsMode
              ? allProjectsMap.get(selectedIssue.project)?.identifier || ''
              : selectedProject?.identifier || ''
          }
          projectName={
            isAllProjectsMode
              ? allProjectsMap.get(selectedIssue.project)?.name || ''
              : selectedProject?.name || ''
          }
          states={states}
          labels={labels}
          onClose={() => setSelectedIssue(null)}
          onUpdate={() => {
            if (isAllProjectsMode) {
              fetchIssues('all')
            } else if (selectedProject) {
              fetchIssues(selectedProject.id)
            }
          }}
        />
      )}
    </div>
  )
}

// ============ ISSUE CARD COMPONENT (Plane-style with property badges) ============
function IssueCard({
  issue,
  projectIdentifier,
  projectName,
  projectColor,
  showProject = false,
  onClick
}: {
  issue: PlaneIssue
  projectIdentifier?: string
  projectName?: string
  projectColor?: string
  showProject?: boolean
  onClick: () => void
}) {
  const priorityConfig = getPriorityConfig(issue.priority)
  const stateConfig = getStateGroupConfig(issue.state_detail?.group || 'unstarted')
  const PriorityIcon = priorityConfig.icon
  const StateIcon = stateConfig.icon

  return (
    <div
      onClick={onClick}
      className="group bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-3 cursor-pointer transition-all duration-200"
      style={{
        ['--glow-color' as any]: projectColor || 'var(--primary)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = projectColor || 'var(--border-strong)'
        e.currentTarget.style.boxShadow = `0 0 12px ${projectColor}40, 0 0 4px ${projectColor}20`
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = ''
        e.currentTarget.style.boxShadow = ''
      }}
    >
      {/* Header Row: Title + Project Name */}
      <div className="flex items-start justify-between gap-2 mb-1">
        {/* Task Title */}
        <h4 className="text-sm font-medium text-[var(--text-main)] line-clamp-1 leading-snug flex-1">
          {issue.name}
        </h4>
        {/* Project Name - Top Right */}
        <div
          className="text-[10px] font-medium shrink-0"
          style={{ color: projectColor || 'var(--text-muted)' }}
        >
          {projectName || projectIdentifier}
        </div>
      </div>

      {/* Description Preview (2 lines) */}
      {stripHtml(issue.description_html) && (
        <p className="text-xs text-[var(--text-muted)] mb-3 line-clamp-2 leading-relaxed">
          {stripHtml(issue.description_html)}
        </p>
      )}
      {!stripHtml(issue.description_html) && <div className="mb-3" />}

      {/* Property Badges Row - Plane style */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* State Badge */}
        <div
          className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-[var(--border)]"
          title={issue.state_detail?.name}
        >
          <StateIcon size={12} style={{ color: stateConfig.color }} />
          <span className="text-[var(--text-muted)]">{issue.state_detail?.name || 'Todo'}</span>
        </div>

        {/* Priority Badge */}
        <div
          className="flex items-center justify-center w-6 h-6 rounded border border-[var(--border)]"
          title={priorityConfig.label}
        >
          <PriorityIcon size={14} style={{ color: priorityConfig.color }} />
        </div>

        {/* Due Date Badge */}
        {issue.target_date && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-[var(--border)] text-[var(--text-muted)]">
            <Calendar size={12} />
            <span>{new Date(issue.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          </div>
        )}

        {/* Assignee Avatars */}
        {issue.assignees && issue.assignees.length > 0 && (
          <div className="flex -space-x-1">
            {issue.assignees.slice(0, 2).map((assignee, idx) => (
              <div
                key={assignee.id}
                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] text-white font-medium border-2 border-[var(--bg-card)]"
                style={{
                  background: `linear-gradient(135deg, hsl(${(idx * 60) + 220}, 70%, 50%), hsl(${(idx * 60) + 280}, 70%, 50%))`
                }}
                title={`${assignee.first_name} ${assignee.last_name}`}
              >
                {assignee.first_name?.[0]}{assignee.last_name?.[0]}
              </div>
            ))}
          </div>
        )}

        {/* Cycle/Module Badge (placeholder) */}
        {issue.sub_issues_count && issue.sub_issues_count > 0 && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-[var(--border)] text-[var(--text-muted)]">
            <Layers size={12} />
            <span>{issue.sub_issues_count}</span>
          </div>
        )}

        {/* Attachments */}
        {issue.attachment_count > 0 && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-[var(--border)] text-[var(--text-muted)]">
            <Paperclip size={12} />
            <span>{issue.attachment_count}</span>
          </div>
        )}

        {/* Links */}
        {issue.link_count > 0 && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-[var(--border)] text-[var(--text-muted)]">
            <Link2 size={12} />
            <span>{issue.link_count}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ============ QUICK ADD TASK COMPONENT ============
function QuickAddIssue({
  onSubmit,
  onCancel
}: {
  onSubmit: (name: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (name.trim()) {
      onSubmit(name.trim())
      setName('')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-3">
      <input
        ref={inputRef}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Issue title..."
        className="w-full bg-transparent text-sm text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none mb-2"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel()
        }}
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-main)]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!name.trim()}
          className="px-3 py-1 bg-[var(--primary)] text-white text-xs rounded hover:opacity-90 disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </form>
  )
}

// ============ CREATE TASK MODAL ============
function CreateTaskModal({
  projects,
  states,
  labels,
  defaultProjectId,
  onClose,
  onCreate,
}: {
  projects: PlaneProject[]
  states: PlaneState[]
  labels: PlaneLabel[]
  defaultProjectId?: string
  onClose: () => void
  onCreate: (data: {
    projectId: string
    name: string
    description?: string
    priority?: string
    state?: string
    labels?: string[]
    start_date?: string
    target_date?: string
  }) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('none')
  const [stateId, setStateId] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId || projects[0]?.id || '')
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])
  const [startDate, setStartDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [createMore, setCreateMore] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  const selectedProject = projects.find(p => p.id === selectedProjectId)

  const handleCreate = () => {
    onCreate({
      projectId: selectedProjectId,
      name,
      description,
      priority,
      state: stateId,
      labels: selectedLabels,
      start_date: startDate || undefined,
      target_date: dueDate || undefined,
    })
    if (createMore) {
      setName('')
      setDescription('')
    }
  }

  const toggleLabel = (labelId: string) => {
    setSelectedLabels(prev =>
      prev.includes(labelId)
        ? prev.filter(id => id !== labelId)
        : [...prev, labelId]
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center pt-[8vh] z-50 animate-fade-in">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl w-full max-w-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h3 className="text-base font-medium text-[var(--text-muted)]">Create new task</h3>
          <button
            onClick={onClose}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Project Selector */}
          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === 'project' ? null : 'project')}
              className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-sm hover:border-[var(--border-strong)] transition-colors"
            >
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: getProjectColor(selectedProject?.name || '') }}
              />
              <span className="text-[var(--text-main)]">{selectedProject?.name || 'Select project'}</span>
              <ChevronDown size={14} className="text-[var(--text-muted)]" />
            </button>

            {openDropdown === 'project' && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50">
                <div className="p-2 max-h-60 overflow-y-auto">
                  {projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => {
                        setSelectedProjectId(project.id)
                        setOpenDropdown(null)
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
                        selectedProjectId === project.id
                          ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                          : 'text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                      }`}
                    >
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: getProjectColor(project.name) }}
                      />
                      <span>{project.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Title"
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary)]"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Click to add description"
              rows={4}
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] rounded-lg px-4 py-3 text-sm text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary)] resize-none"
            />
          </div>

          {/* Properties Row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* State */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'state' ? null : 'state')}
                className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-main)] hover:border-[var(--border-strong)] transition-colors"
              >
                {(() => {
                  const state = states.find(s => s.id === stateId)
                  const config = getStateGroupConfig(state?.group || 'backlog')
                  const Icon = config.icon
                  return <Icon size={14} style={{ color: config.color }} />
                })()}
                <span>{states.find(s => s.id === stateId)?.name || 'Backlog'}</span>
              </button>

              {openDropdown === 'state' && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50">
                  <div className="p-2 max-h-60 overflow-y-auto">
                    {states.map(state => {
                      const config = getStateGroupConfig(state.group)
                      const Icon = config.icon
                      return (
                        <button
                          key={state.id}
                          onClick={() => {
                            setStateId(state.id)
                            setOpenDropdown(null)
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                            stateId === state.id
                              ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                              : 'text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                          }`}
                        >
                          <Icon size={14} style={{ color: config.color }} />
                          <span>{state.name}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'priority' ? null : 'priority')}
                className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-main)] hover:border-[var(--border-strong)] transition-colors"
              >
                {(() => {
                  const config = getPriorityConfig(priority)
                  const Icon = config.icon
                  return <Icon size={14} style={{ color: config.color }} />
                })()}
                <span>{getPriorityConfig(priority).label}</span>
              </button>

              {openDropdown === 'priority' && (
                <div className="absolute top-full left-0 mt-1 w-36 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    {(['none', 'low', 'medium', 'high', 'urgent'] as const).map(p => {
                      const config = getPriorityConfig(p)
                      const Icon = config.icon
                      return (
                        <button
                          key={p}
                          onClick={() => {
                            setPriority(p)
                            setOpenDropdown(null)
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                            priority === p
                              ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                              : 'text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                          }`}
                        >
                          <Icon size={14} style={{ color: config.color }} />
                          <span>{config.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Labels */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'labels' ? null : 'labels')}
                className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-main)] hover:border-[var(--border-strong)] transition-colors"
              >
                <Tag size={14} />
                <span>{selectedLabels.length > 0 ? `${selectedLabels.length} Labels` : 'Labels'}</span>
              </button>

              {openDropdown === 'labels' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50">
                  <div className="p-2 max-h-60 overflow-y-auto">
                    {labels.length === 0 ? (
                      <p className="text-xs text-[var(--text-muted)] px-2 py-1">No labels available</p>
                    ) : (
                      labels.map(label => (
                        <button
                          key={label.id}
                          onClick={() => toggleLabel(label.id)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                            selectedLabels.includes(label.id)
                              ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                              : 'text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                          }`}
                        >
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: label.color }}
                          />
                          <span>{label.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Start Date */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'start' ? null : 'start')}
                className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-main)] hover:border-[var(--border-strong)] transition-colors"
              >
                <Calendar size={14} />
                <span>{startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Start date'}</span>
              </button>

              {openDropdown === 'start' && (
                <div className="absolute top-full left-0 mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50 p-3">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value)
                      setOpenDropdown(null)
                    }}
                    className="bg-[var(--bg-element)] border border-[var(--border)] rounded px-2 py-1 text-sm text-[var(--text-main)]"
                  />
                </div>
              )}
            </div>

            {/* Due Date */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'due' ? null : 'due')}
                className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-main)] hover:border-[var(--border-strong)] transition-colors"
              >
                <CalendarDays size={14} />
                <span>{dueDate ? new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Due date'}</span>
              </button>

              {openDropdown === 'due' && (
                <div className="absolute top-full left-0 mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50 p-3">
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => {
                      setDueDate(e.target.value)
                      setOpenDropdown(null)
                    }}
                    className="bg-[var(--bg-element)] border border-[var(--border)] rounded px-2 py-1 text-sm text-[var(--text-main)]"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border)]">
          {/* Create More Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setCreateMore(!createMore)}
              className={`w-9 h-5 rounded-full transition-colors ${
                createMore ? 'bg-[var(--primary)]' : 'bg-[var(--bg-element)]'
              }`}
            >
              <div
                className={`w-4 h-4 rounded-full bg-white shadow transition-transform mt-0.5 ${
                  createMore ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
                }`}
              />
            </div>
            <span className="text-xs text-[var(--text-muted)]">Create more</span>
          </label>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
            >
              Discard
            </button>
            <button
              onClick={handleCreate}
              disabled={!name.trim() || !selectedProjectId}
              className="px-4 py-2 bg-[var(--primary)] text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============ ISSUE DETAIL SIDE PANEL (Plane-style drawer with full edit) ============
function IssueDetailModal({
  issue,
  projectIdentifier,
  projectName,
  states,
  labels,
  onClose,
  onUpdate,
}: {
  issue: PlaneIssue
  projectIdentifier: string
  projectName: string
  states: PlaneState[]
  labels: PlaneLabel[]
  onClose: () => void
  onUpdate: () => void
}) {
  const client = getPlaneClient()
  const { adminUser } = useAuth()

  // Get current user's display info
  const currentUserName = adminUser?.firstName && adminUser?.lastName
    ? `${adminUser.firstName} ${adminUser.lastName}`
    : adminUser?.firstName || adminUser?.email || 'You'
  const currentUserInitial = adminUser?.firstName?.[0] || adminUser?.email?.[0] || 'U'

  // Editable state
  const [title, setTitle] = useState(issue.name)
  const [description, setDescription] = useState(stripHtml(issue.description_html))
  const [currentStateId, setCurrentStateId] = useState(issue.state)
  const [currentPriority, setCurrentPriority] = useState(issue.priority)
  const [startDate, setStartDate] = useState(issue.start_date || '')
  const [dueDate, setDueDate] = useState(issue.target_date || '')
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>(
    issue.labels?.map(l => l.id) || []
  )

  // Comments state
  const [comments, setComments] = useState<any[]>([])
  const [newComment, setNewComment] = useState('')
  const [isLoadingComments, setIsLoadingComments] = useState(true)
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)

  // UI state
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Fetch comments on mount
  useEffect(() => {
    const fetchComments = async () => {
      setIsLoadingComments(true)
      try {
        const { data } = await client.getIssueComments(issue.project, issue.id)
        // Handle different response formats
        if (data) {
          if (Array.isArray(data)) {
            setComments(data)
          } else if ((data as any).results && Array.isArray((data as any).results)) {
            setComments((data as any).results)
          } else {
            setComments([])
          }
        }
      } catch (err) {
        console.error('Failed to fetch comments:', err)
        setComments([])
      }
      setIsLoadingComments(false)
    }
    fetchComments()
  }, [issue.id, issue.project])

  // Submit comment handler
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return

    setIsSubmittingComment(true)
    try {
      const { data, error } = await client.createIssueComment(issue.project, issue.id, newComment.trim())

      if (!error && data) {
        // Ensure the comment has required fields for rendering
        // Use the Nexus admin user's name for display
        const newCommentData = {
          ...data,
          id: data.id || Date.now().toString(),
          created_at: data.created_at || new Date().toISOString(),
          comment_html: data.comment_html || `<p>${newComment.trim()}</p>`,
          // Override with Nexus user info
          actor_detail: {
            first_name: adminUser?.firstName || 'You',
            last_name: adminUser?.lastName || '',
          },
        }
        // Safely append to array
        setComments(prev => Array.isArray(prev) ? [...prev, newCommentData] : [newCommentData])
        setNewComment('')
      }
    } catch (err) {
      console.error('Failed to submit comment:', err)
    }
    setIsSubmittingComment(false)
  }

  // Get current state details
  const currentState = states.find(s => s.id === currentStateId) || issue.state_detail
  const stateConfig = getStateGroupConfig(currentState?.group || 'unstarted')
  const priorityConfig = getPriorityConfig(currentPriority)
  const StateIcon = stateConfig.icon
  const PriorityIcon = priorityConfig.icon

  // Handle escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false)
        } else if (showMoreMenu) {
          setShowMoreMenu(false)
        } else if (openDropdown) {
          setOpenDropdown(null)
        } else if (!isEditingTitle && !isEditingDescription) {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose, openDropdown, isEditingTitle, isEditingDescription, showMoreMenu, showDeleteConfirm])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openDropdown && !(e.target as Element).closest('.dropdown-container')) {
        setOpenDropdown(null)
      }
      if (showMoreMenu && !(e.target as Element).closest('.more-menu-container')) {
        setShowMoreMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openDropdown, showMoreMenu])

  // Update issue API call
  const updateIssue = async (updates: Partial<PlaneIssue>) => {
    setIsSaving(true)
    const { error } = await client.updateIssue(issue.project, issue.id, updates)
    setIsSaving(false)
    if (!error) {
      onUpdate()
    }
    return !error
  }

  // Handlers
  const handleTitleSave = async () => {
    if (title.trim() && title !== issue.name) {
      await updateIssue({ name: title.trim() })
    }
    setIsEditingTitle(false)
  }

  const handleDescriptionSave = async () => {
    if (description !== stripHtml(issue.description_html)) {
      await updateIssue({ description_html: description })
    }
    setIsEditingDescription(false)
  }

  const handleStateChange = async (stateId: string) => {
    setCurrentStateId(stateId)
    setOpenDropdown(null)
    await updateIssue({ state: stateId })
  }

  const handlePriorityChange = async (priority: string) => {
    setCurrentPriority(priority as any)
    setOpenDropdown(null)
    await updateIssue({ priority: priority as any })
  }

  const handleStartDateChange = async (date: string) => {
    setStartDate(date)
    await updateIssue({ start_date: date || null })
  }

  const handleDueDateChange = async (date: string) => {
    setDueDate(date)
    await updateIssue({ target_date: date || null })
  }

  const handleLabelToggle = async (labelId: string) => {
    const newLabelIds = selectedLabelIds.includes(labelId)
      ? selectedLabelIds.filter(id => id !== labelId)
      : [...selectedLabelIds, labelId]
    setSelectedLabelIds(newLabelIds)
    await updateIssue({ labels: newLabelIds as any })
  }

  const handleDeleteIssue = async () => {
    console.log('=== DELETE CLICKED ===')
    console.log('Issue object:', issue)
    console.log('Deleting issue:', { projectId: issue.project, issueId: issue.id })
    setIsDeleting(true)
    try {
      const { error } = await client.deleteIssue(issue.project, issue.id)
      console.log('Delete result:', { error })
      setIsDeleting(false)

      if (error) {
        console.error('Delete error:', error)
        alert(`Failed to delete issue: ${error.detail || error.error}`)
      } else {
        onUpdate() // Refresh the issues list
        onClose()  // Close the modal
      }
    } catch (err) {
      console.error('Delete exception:', err)
      setIsDeleting(false)
      alert(`Failed to delete issue: ${err}`)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
        onClick={onClose}
      />

      {/* Side Panel */}
      <div
        className="fixed top-0 right-0 bottom-0 w-full md:w-[50%] lg:w-[45%] xl:w-[40%] bg-[var(--bg-card)] border-l border-[var(--border)] z-50 flex flex-col animate-slide-in-right"
        style={{
          boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.12), 0px 6px 12px 0px rgba(16, 24, 40, 0.12), 0px 1px 16px 0px rgba(16, 24, 40, 0.12)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors"
              title="Close"
            >
              <ArrowRight size={16} />
            </button>
            <a
              href={`${PLANE_BASE_URL}/${PLANE_WORKSPACE}/projects/${issue.project}/issues/${issue.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors"
              title="Open in Plane"
            >
              <Maximize2 size={16} />
            </a>
            {isSaving && (
              <span className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" />
                Saving...
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors"
              title="Copy link"
              onClick={() => {
                navigator.clipboard.writeText(`${PLANE_BASE_URL}/${PLANE_WORKSPACE}/projects/${issue.project}/issues/${issue.id}`)
              }}
            >
              <Copy size={16} />
            </button>
            <div className="relative more-menu-container">
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors"
              >
                <MoreHorizontal size={16} />
              </button>

              {/* More Menu Dropdown */}
              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50 py-1">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${projectIdentifier}-${issue.sequence_id}`)
                      setShowMoreMenu(false)
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-main)] hover:bg-[var(--bg-element)] transition-colors"
                  >
                    <Copy size={14} />
                    Copy issue ID
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${PLANE_BASE_URL}/${PLANE_WORKSPACE}/projects/${issue.project}/issues/${issue.id}`)
                      setShowMoreMenu(false)
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-main)] hover:bg-[var(--bg-element)] transition-colors"
                  >
                    <Link2 size={14} />
                    Copy link
                  </button>
                  <a
                    href={`${PLANE_BASE_URL}/${PLANE_WORKSPACE}/projects/${issue.project}/issues/${issue.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-main)] hover:bg-[var(--bg-element)] transition-colors"
                    onClick={() => setShowMoreMenu(false)}
                  >
                    <ExternalLink size={14} />
                    Open in Plane
                  </a>
                  <div className="border-t border-[var(--border)] my-1" />
                  <button
                    onClick={() => {
                      setShowMoreMenu(false)
                      setShowDeleteConfirm(true)
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={14} />
                    Delete issue
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="px-6 py-5 space-y-5">
            {/* Project Name */}
            <div
              className="text-xs font-medium"
              style={{ color: getProjectColor(projectName || '') }}
            >
              {projectName || projectIdentifier}
            </div>

            {/* Editable Title */}
            {isEditingTitle ? (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave()
                  if (e.key === 'Escape') {
                    setTitle(issue.name)
                    setIsEditingTitle(false)
                  }
                }}
                autoFocus
                className="w-full text-lg font-semibold text-[var(--text-main)] bg-transparent border-b-2 border-[var(--primary)] focus:outline-none"
              />
            ) : (
              <h2
                onClick={() => setIsEditingTitle(true)}
                className="text-lg font-semibold text-[var(--text-main)] leading-tight cursor-pointer hover:bg-[var(--bg-element)]/50 px-1 -mx-1 rounded"
              >
                {title}
              </h2>
            )}

            {/* Editable Description */}
            {isEditingDescription ? (
              <div className="space-y-2">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  autoFocus
                  rows={4}
                  className="w-full text-sm text-[var(--text-main)] bg-[var(--bg-element)] border border-[var(--border)] rounded-lg p-3 focus:outline-none focus:border-[var(--primary)] resize-none"
                  placeholder="Add a description..."
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDescriptionSave}
                    className="px-3 py-1 text-xs bg-[var(--primary)] text-white rounded hover:opacity-90"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setDescription(stripHtml(issue.description_html))
                      setIsEditingDescription(false)
                    }}
                    className="px-3 py-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-main)]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => setIsEditingDescription(true)}
                className="min-h-[60px] cursor-pointer hover:bg-[var(--bg-element)]/50 px-2 -mx-2 py-2 rounded"
              >
                {stripHtml(description) ? (
                  <p className="text-sm text-[var(--text-main)] whitespace-pre-wrap">{stripHtml(description)}</p>
                ) : (
                  <p className="text-sm text-[var(--text-muted)]">Add a description...</p>
                )}
              </div>
            )}

            {/* Action Buttons Row */}
            <div className="flex items-center gap-2 py-2 border-y border-[var(--border)]">
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors">
                <Layers size={14} />
                Add sub-issue
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors">
                <Link2 size={14} />
                Add link
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded transition-colors">
                <Paperclip size={14} />
                Attach
              </button>
            </div>

            {/* Properties Section */}
            <div className="space-y-1">
              <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
                Properties
              </h4>

              <div className="space-y-0.5">
                {/* State Dropdown */}
                <div className="relative dropdown-container">
                  <div
                    onClick={() => setOpenDropdown(openDropdown === 'state' ? null : 'state')}
                    className="flex items-center py-2 px-2 rounded hover:bg-[var(--bg-element)]/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-32 flex-shrink-0">
                      <Circle size={14} className="text-[var(--text-muted)]" />
                      <span className="text-xs text-[var(--text-muted)]">State</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <StateIcon size={14} style={{ color: stateConfig.color }} />
                      <span className="text-sm text-[var(--text-main)]">{currentState?.name || 'Todo'}</span>
                      <ChevronDown size={14} className="text-[var(--text-muted)] ml-auto" />
                    </div>
                  </div>
                  {openDropdown === 'state' && (
                    <div className="absolute left-32 top-0 w-48 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50 py-1 max-h-60 overflow-y-auto">
                      {states.map(state => {
                        const config = getStateGroupConfig(state.group)
                        const Icon = config.icon
                        return (
                          <button
                            key={state.id}
                            onClick={() => handleStateChange(state.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-element)] transition-colors ${
                              state.id === currentStateId ? 'bg-[var(--primary)]/10 text-[var(--primary)]' : 'text-[var(--text-main)]'
                            }`}
                          >
                            <Icon size={14} style={{ color: config.color }} />
                            <span>{state.name}</span>
                            {state.id === currentStateId && <CheckCircle2 size={14} className="ml-auto" />}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Priority Dropdown */}
                <div className="relative dropdown-container">
                  <div
                    onClick={() => setOpenDropdown(openDropdown === 'priority' ? null : 'priority')}
                    className="flex items-center py-2 px-2 rounded hover:bg-[var(--bg-element)]/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-32 flex-shrink-0">
                      <BarChart3 size={14} className="text-[var(--text-muted)]" />
                      <span className="text-xs text-[var(--text-muted)]">Priority</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <PriorityIcon size={14} style={{ color: priorityConfig.color }} />
                      <span className="text-sm text-[var(--text-main)]">{priorityConfig.label}</span>
                      <ChevronDown size={14} className="text-[var(--text-muted)] ml-auto" />
                    </div>
                  </div>
                  {openDropdown === 'priority' && (
                    <div className="absolute left-32 top-0 w-40 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50 py-1">
                      {(['urgent', 'high', 'medium', 'low', 'none'] as const).map(p => {
                        const config = getPriorityConfig(p)
                        const Icon = config.icon
                        return (
                          <button
                            key={p}
                            onClick={() => handlePriorityChange(p)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-element)] transition-colors ${
                              p === currentPriority ? 'bg-[var(--primary)]/10 text-[var(--primary)]' : 'text-[var(--text-main)]'
                            }`}
                          >
                            <Icon size={14} style={{ color: config.color }} />
                            <span>{config.label}</span>
                            {p === currentPriority && <CheckCircle2 size={14} className="ml-auto" />}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Assignees (read-only for now) */}
                <div className="flex items-center py-2 px-2 rounded hover:bg-[var(--bg-element)]/50 transition-colors">
                  <div className="flex items-center gap-2 w-32 flex-shrink-0">
                    <Users size={14} className="text-[var(--text-muted)]" />
                    <span className="text-xs text-[var(--text-muted)]">Assignees</span>
                  </div>
                  <div className="flex-1">
                    {issue.assignees && issue.assignees.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-1">
                          {issue.assignees.slice(0, 3).map((assignee, idx) => (
                            <div
                              key={assignee.id}
                              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] text-white font-medium border-2 border-[var(--bg-card)]"
                              style={{
                                background: `linear-gradient(135deg, hsl(${(idx * 60) + 220}, 70%, 50%), hsl(${(idx * 60) + 280}, 70%, 50%))`
                              }}
                              title={`${assignee.first_name} ${assignee.last_name}`}
                            >
                              {assignee.first_name?.[0]}{assignee.last_name?.[0]}
                            </div>
                          ))}
                        </div>
                        <span className="text-sm text-[var(--text-main)]">
                          {issue.assignees.length === 1
                            ? `${issue.assignees[0].first_name} ${issue.assignees[0].last_name}`
                            : `${issue.assignees.length} assignees`}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-[var(--text-muted)]">Add assignee</span>
                    )}
                  </div>
                </div>

                {/* Start Date */}
                <div className="flex items-center py-2 px-2 rounded hover:bg-[var(--bg-element)]/50 transition-colors">
                  <div className="flex items-center gap-2 w-32 flex-shrink-0">
                    <CalendarDays size={14} className="text-[var(--text-muted)]" />
                    <span className="text-xs text-[var(--text-muted)]">Start date</span>
                  </div>
                  <div className="flex-1">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      className="bg-transparent text-sm text-[var(--text-main)] focus:outline-none cursor-pointer"
                    />
                  </div>
                </div>

                {/* Due Date */}
                <div className="flex items-center py-2 px-2 rounded hover:bg-[var(--bg-element)]/50 transition-colors">
                  <div className="flex items-center gap-2 w-32 flex-shrink-0">
                    <Calendar size={14} className="text-[var(--text-muted)]" />
                    <span className="text-xs text-[var(--text-muted)]">Due date</span>
                  </div>
                  <div className="flex-1">
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => handleDueDateChange(e.target.value)}
                      className={`bg-transparent text-sm focus:outline-none cursor-pointer ${
                        dueDate && new Date(dueDate) < new Date() ? 'text-red-400' : 'text-[var(--text-main)]'
                      }`}
                    />
                  </div>
                </div>

                {/* Labels Dropdown */}
                <div className="relative dropdown-container">
                  <div
                    onClick={() => setOpenDropdown(openDropdown === 'labels' ? null : 'labels')}
                    className="flex items-center py-2 px-2 rounded hover:bg-[var(--bg-element)]/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-32 flex-shrink-0">
                      <Tag size={14} className="text-[var(--text-muted)]" />
                      <span className="text-xs text-[var(--text-muted)]">Labels</span>
                    </div>
                    <div className="flex-1 flex items-center gap-1 flex-wrap">
                      {selectedLabelIds.length > 0 ? (
                        labels.filter(l => selectedLabelIds.includes(l.id)).map(label => (
                          <span
                            key={label.id}
                            className="text-xs px-2 py-0.5 rounded"
                            style={{
                              backgroundColor: label.color + '20',
                              color: label.color
                            }}
                          >
                            {label.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-[var(--text-muted)]">Add label</span>
                      )}
                      <ChevronDown size={14} className="text-[var(--text-muted)] ml-auto" />
                    </div>
                  </div>
                  {openDropdown === 'labels' && (
                    <div className="absolute left-32 top-0 w-56 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg z-50 py-1 max-h-60 overflow-y-auto">
                      {labels.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-[var(--text-muted)]">No labels available</div>
                      ) : (
                        labels.map(label => (
                          <button
                            key={label.id}
                            onClick={() => handleLabelToggle(label.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-element)] transition-colors text-[var(--text-main)]"
                          >
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: label.color }}
                            />
                            <span className="flex-1 text-left">{label.name}</span>
                            {selectedLabelIds.includes(label.id) && (
                              <CheckCircle2 size={14} className="text-[var(--primary)]" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Activity Section */}
            <div className="space-y-3 pt-4 border-t border-[var(--border)]">
              <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                Activity & Comments
              </h4>

              <div className="space-y-3">
                {/* Issue Created Event */}
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-6 h-6 rounded-full bg-[var(--bg-element)] flex items-center justify-center">
                      <Plus size={12} className="text-[var(--text-muted)]" />
                    </div>
                    <div className="w-px flex-1 bg-[var(--border)] mt-1" />
                  </div>
                  <div className="flex-1 pb-3">
                    <p className="text-sm text-[var(--text-main)]">
                      <span className="font-medium">Issue created</span>
                    </p>
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">
                      {new Date(issue.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {/* Comments */}
                {isLoadingComments ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-[var(--text-muted)]">
                    <Loader2 size={14} className="animate-spin" />
                    Loading comments...
                  </div>
                ) : comments && comments.length > 0 ? (
                  comments.map((comment, index) => {
                    if (!comment) return null
                    const authorName = comment.actor_detail?.first_name || comment.actor?.display_name || 'User'
                    const authorInitial = authorName[0] || 'U'
                    const commentDate = comment.created_at ? new Date(comment.created_at) : new Date()
                    const commentContent = comment.comment_html || comment.comment || ''

                    return (
                      <div key={comment.id || `comment-${index}`} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-[10px] text-white font-medium">
                            {authorInitial}
                          </div>
                          {index < comments.length - 1 && (
                            <div className="w-px flex-1 bg-[var(--border)] mt-1" />
                          )}
                        </div>
                        <div className="flex-1 pb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[var(--text-main)]">
                              {authorName} {comment.actor_detail?.last_name || ''}
                            </span>
                            <span className="text-xs text-[var(--text-muted)]">
                              {commentDate.toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {commentContent && (
                            <div
                              className="text-sm text-[var(--text-main)] mt-1 prose prose-sm prose-invert max-w-none"
                              dangerouslySetInnerHTML={{ __html: commentContent }}
                            />
                          )}
                        </div>
                      </div>
                    )
                  })
                ) : null}

                {/* Updated Event */}
                {issue.updated_at !== issue.created_at && (
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--bg-element)] flex items-center justify-center">
                      <FileText size={12} className="text-[var(--text-muted)]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[var(--text-main)]">
                        <span className="font-medium">Last updated</span>
                      </p>
                      <p className="text-xs text-[var(--text-muted)] mt-0.5">
                        {new Date(issue.updated_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Comment Input */}
              <div className="mt-4">
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[var(--primary)] to-purple-500 flex items-center justify-center text-[10px] text-white font-medium flex-shrink-0 uppercase">
                    {currentUserInitial}
                  </div>
                  <div className="flex-1 space-y-2">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary)] resize-none"
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          handleSubmitComment()
                        }
                      }}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[var(--text-muted)]">
                        Press ⌘+Enter to submit
                      </span>
                      <button
                        onClick={handleSubmitComment}
                        disabled={!newComment.trim() || isSubmittingComment}
                        className="px-3 py-1.5 text-xs font-medium bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        {isSubmittingComment ? (
                          <>
                            <Loader2 size={12} className="animate-spin" />
                            Posting...
                          </>
                        ) : (
                          'Comment'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] animate-fade-in">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <Trash2 size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-main)]">Delete Issue</h3>
                <p className="text-sm text-[var(--text-muted)]">
                  {projectIdentifier}-{issue.sequence_id}
                </p>
              </div>
            </div>

            <p className="text-sm text-[var(--text-main)] mb-6">
              Are you sure you want to delete this issue? This action cannot be undone and will permanently remove the issue and all its data.
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteIssue}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    Delete Issue
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProjectsView
